import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import LineCrownIcon from 'LuxKit/Icons/line/LineCrownIcon'
import LineShieldCheckIcon from 'LuxKit/Icons/line/LineShieldCheckIcon'
import LayoutContainer from 'components/Common/LayoutContainer'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Group from 'components/utils/Group'
import { rem } from 'polished'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { LOYALTY_CREDIT_CARD_LAYOUT_CONTAINER_CLASS } from './loyaltyCreditCardConstants'
import { LoyaltyCreditCardCreamSection, loyaltyCreditCardCardShadow } from './components/loyaltyCreditCardStyles'
import LoyaltyCreditCardApplyNowButton from './components/LoyaltyCreditCardApplyNowButton'
import { LOYALTY_CREDIT_CARD_APPLY_NOW_PLACEMENTS } from 'analytics/snowplow/events/loyaltyCreditCardEvents'
import LineChairWithAirplaneIcon from 'LuxKit/Icons/line/LineChairWithAirplaneIcon'
import LineGlobeAltIcon from 'LuxKit/Icons/line/LineGlobeAltIcon'

const BenefitGrid = styled.div`
  display: grid;
  gap: ${rem(20)};
  grid-template-columns: 1fr;

  ${mediaQueryUp.tablet} {
    grid-template-columns: repeat(2, 1fr);
  }
`

const BenefitCard = styled.div`
  background: white;
  border-radius: ${rem(8)};
  padding-block: ${rem(20)};
  padding-inline: ${rem(20)} ${rem(32)};
  display: flex;
  gap: ${rem(16)};
  align-items: flex-start;
  ${loyaltyCreditCardCardShadow}
`

const BenefitIcon = styled.div`
  width: ${rem(48)};
  height: ${rem(48)};
  border-radius: 50%;
  background: ${props => props.theme.palette.highlight.primary.darkBackground};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${props => props.theme.palette.highlight.primary.darkContrast};
  font-size: ${rem(20)};
`

const BenefitText = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(4)};
`

const TravelBenefitsApplyButton = styled(LoyaltyCreditCardApplyNowButton)`
  && {
    min-width: ${rem(148)};
  }
`

function LoyaltyCreditCardTravelBenefitsSection() {
  const intl = useIntl()

  return (
    <LoyaltyCreditCardCreamSection background="light-background" id="travel-perks">
      <LayoutContainer className={LOYALTY_CREDIT_CARD_LAYOUT_CONTAINER_CLASS} variant="medium">
        <VerticalSpacer gap={40}>
          <VerticalSpacer gap={8}>
            <Heading variant="heading1" align="center">
              {intl.formatMessage(
                {
                  id: 'x1CaBmBa9rI6wVj3kixf',
                  defaultMessage:
                    'The {societyItalic} Credit Card – designed to make <lineBreak></lineBreak> every {escapeItalic} smoother',
                  description: 'Travel benefits section heading on Société Credit Card page',
                },
                {
                  societyItalic: (
                    <em>
                      {intl.formatMessage({
                        id: 'pv0nUoDuDULhyXoljjCs',
                        defaultMessage: 'Société',
                        description: 'Italic Société emphasis in the travel benefits heading on the credit card page',
                      })}
                    </em>
                  ),
                  escapeItalic: (
                    <em>
                      {intl.formatMessage({
                        id: 'aMhxGba1q0EhR-I5hGMy',
                        defaultMessage: 'escape',
                        description: 'Italic escape emphasis in the travel benefits heading on the credit card page',
                      })}
                    </em>
                  ),
                  lineBreak: () => <br key="line-break" />,
                },
              )}
            </Heading>
            <BodyText variant="medium" align="center">
              {intl.formatMessage({
                id: 'aEfmAODa9GQnwAiQT4bo',
                defaultMessage: 'The Société Credit Card comes loaded with travel benefits that make every trip smoother.',
                description: 'Travel benefits section subtitle on Société Credit Card page',
              })}
            </BodyText>
          </VerticalSpacer>

          <BenefitGrid>
            <BenefitCard>
              <BenefitIcon aria-hidden="true">
                <LineCrownIcon size="M" />
              </BenefitIcon>
              <BenefitText>
                <BodyText variant="large" weight="bold">
                  {intl.formatMessage({
                    id: 'GHxJTyKCZAOu4PATEl0_',
                    defaultMessage: 'Use your points. No restrictions.',
                    description: 'Heading for the first travel benefit card on the Société Credit Card page',
                  })}
                </BodyText>
                <BodyText variant="medium" colour="neutral-two">
                  {intl.formatMessage({
                    id: 'N70pLeA8eAo3Ie8aCUpz',
                    defaultMessage: 'Redeem your Société Points on any Luxury Escapes booking — hotels, flights, tours or cruises. No blackout dates, no restrictions.',
                    description: 'Body copy for the first travel benefit card on the Société Credit Card page',
                  })}
                </BodyText>
              </BenefitText>
            </BenefitCard>

            <BenefitCard>
              <BenefitIcon aria-hidden="true">
                <LineShieldCheckIcon size="M" />
              </BenefitIcon>
              <BenefitText>
                <BodyText variant="large" weight="bold">
                  {intl.formatMessage({
                    id: 'nUxXshDZvPHj7RMnsyGy',
                    defaultMessage: 'Travel Insurance When You Need It',
                    description: 'Heading for the second travel benefit card on the Société Credit Card page',
                  })}
                </BodyText>
                <BodyText variant="medium" colour="neutral-two">
                  {intl.formatMessage({
                    id: '-yFNk6aluOEz2RIS6CxC',
                    defaultMessage: 'Covered for travel delays, lost luggage, and more — up to $3,000⁴.',
                    description: 'Body copy for the second travel benefit card on the Société Credit Card page',
                  })}
                </BodyText>
              </BenefitText>
            </BenefitCard>

            <BenefitCard>
              <BenefitIcon aria-hidden="true">
                <LineGlobeAltIcon size="M" />
              </BenefitIcon>
              <BenefitText>
                <BodyText variant="large" weight="bold">
                  {intl.formatMessage({
                    id: '-e5VmUHvpkLNey1vkvSz',
                    defaultMessage: '0% International Transaction Fees',
                    description: 'Heading for the third travel benefit card on the Société Credit Card page',
                  })}
                </BodyText>
                <BodyText variant="medium" colour="neutral-two">
                  {intl.formatMessage({
                    id: '-iC2WXuu8CdBHO1_ipx4',
                    defaultMessage: 'Spend abroad without the surcharge. No 3% foreign currency fee — ever. What you spend is what you pay.',
                    description: 'Body copy for the third travel benefit card on the Société Credit Card page',
                  })}
                </BodyText>
              </BenefitText>
            </BenefitCard>

            <BenefitCard>
              <BenefitIcon aria-hidden="true">
                <LineChairWithAirplaneIcon size="M" />
              </BenefitIcon>
              <BenefitText>
                <BodyText variant="large" weight="bold">
                  {intl.formatMessage({
                    id: 'SLgkHAiiYs4Q3_kQ_Bfk',
                    defaultMessage: 'A Better Place to Wait',
                    description: 'Heading for the fourth travel benefit card on the Société Credit Card page',
                  })}
                </BodyText>
                <BodyText variant="medium" colour="neutral-two">
                  {intl.formatMessage({
                    id: 'Q0eRmBB7-LULfzPShBc4',
                    defaultMessage: "Delayed? You're in the lounge. Complimentary lounge access when your flight is delayed.⁴",
                    description: 'Body copy for the fourth travel benefit card on the Société Credit Card page',
                  })}
                </BodyText>
              </BenefitText>
            </BenefitCard>
          </BenefitGrid>

          <Group direction="horizontal" horizontalAlign="center">
            <TravelBenefitsApplyButton fit="mobile-full-width" placement={LOYALTY_CREDIT_CARD_APPLY_NOW_PLACEMENTS.travelBenefits} />
          </Group>
        </VerticalSpacer>
      </LayoutContainer>
    </LoyaltyCreditCardCreamSection>
  )
}

export default LoyaltyCreditCardTravelBenefitsSection
